'use client';

import {
  type ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ChevronDown, Search, TriangleAlert, X } from 'lucide-react';
import ExpandArrowIcon from '../icons/expand-arrow-icon';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { cn } from '../cn';
import {
  SELECT_CHIP_LABEL_CLASSNAME,
  SELECT_CHIP_TRIGGER_CLASSNAME,
} from './select-trigger-styles';

export type MultiSelectOption = {
  disabled?: boolean;
  label: string;
  value: string;
};

export type MultiSelectProps = {
  className?: string;
  clearable?: boolean;
  contentClassName?: string;
  disabled?: boolean;
  emptyText?: string;
  icon?: ReactNode;
  isChip?: boolean;
  isSearch?: boolean;
  onSearchAPI?: (query: string) => Promise<MultiSelectOption[]>;
  onValueChange: (value: string[]) => void;
  options: MultiSelectOption[];
  placeholder?: string;
  placeholderClassName?: string;
  searchPlaceholder?: string;
  showCount?: boolean;
  showSelectedAsTags?: boolean;
  showSelectedCountBadge?: boolean;
  maxSelections?: number;
  maxSelectionMessage?: string;
  tagVariant?: 'pill' | 'outlined';
  variant?: 'default' | 'filter';
  value: string[];
};

const normalizeSearchText = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('vi-VN');

export function MultiSelect({
  className,
  clearable = false,
  contentClassName,
  disabled = false,
  emptyText = 'Không tìm thấy lựa chọn phù hợp.',
  icon,
  isChip = false,
  isSearch = false,
  onSearchAPI,
  onValueChange,
  options,
  placeholder = 'Chọn',
  placeholderClassName,
  searchPlaceholder = 'Tìm kiếm...',
  showCount = true,
  showSelectedAsTags = false,
  showSelectedCountBadge = false,
  maxSelections,
  maxSelectionMessage,
  tagVariant = 'pill',
  variant = 'default',
  value,
}: MultiSelectProps) {
  const inputId = useId();
  const listboxId = `${inputId}-listbox`;
  const onSearchAPIRef = useRef(onSearchAPI);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [apiOptions, setApiOptions] = useState<MultiSelectOption[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [openOptionOrder, setOpenOptionOrder] = useState<string[]>([]);
  const isFilterVariant = variant === 'filter';
  const mergedOptions = useMemo(() => {
    if (!onSearchAPI) return options;

    const optionMap = new Map(
      [...options, ...apiOptions].map(option => [option.value, option])
    );

    return Array.from(optionMap.values());
  }, [apiOptions, onSearchAPI, options]);
  const orderedOptions = useMemo(() => {
    if (!isFilterVariant || !isOpen || openOptionOrder.length === 0) {
      return mergedOptions;
    }

    const indexByValue = new Map(
      openOptionOrder.map((optionValue, index) => [optionValue, index])
    );

    return [...mergedOptions].sort(
      (firstOption, secondOption) =>
        (indexByValue.get(firstOption.value) ?? Number.MAX_SAFE_INTEGER) -
        (indexByValue.get(secondOption.value) ?? Number.MAX_SAFE_INTEGER)
    );
  }, [isFilterVariant, isOpen, mergedOptions, openOptionOrder]);
  const selectedValues = useMemo(() => new Set(value), [value]);
  const selectedOptions = useMemo(() => {
    const optionsByValue = new Map(
      mergedOptions.map(option => [option.value, option])
    );

    return value.flatMap(optionValue => {
      const option = optionsByValue.get(optionValue);

      return option ? [option] : [];
    });
  }, [mergedOptions, value]);
  const filteredOptions = useMemo(() => {
    const normalizedQuery = normalizeSearchText(searchQuery.trim());

    if (onSearchAPI) {
      if (normalizedQuery) {
        return apiOptions;
      }

      return orderedOptions;
    }

    if (!isSearch || !normalizedQuery) return orderedOptions;

    return orderedOptions.filter(option =>
      normalizeSearchText(option.label).includes(normalizedQuery)
    );
  }, [apiOptions, isSearch, onSearchAPI, orderedOptions, searchQuery]);
  const selectedOption = selectedOptions[0];
  const hasSelectedFilteredOptions = filteredOptions.some(option =>
    selectedValues.has(option.value)
  );
  const canClear = clearable && value.length > 0;
  const isMaxSelectionsReached =
    typeof maxSelections === 'number' && value.length >= maxSelections;
  const triggerLabel =
    selectedOptions.length === 0
      ? placeholder
      : selectedOptions.length === 1
        ? `${selectedOption.label}${showCount && !isFilterVariant ? ' (1)' : ''}`
        : showSelectedCountBadge
          ? selectedOption.label
          : `${placeholder}${showCount ? ` (${selectedOptions.length})` : ''}`;

  const toggleValue = (optionValue: string, checked: boolean) => {
    if (checked) {
      if (selectedValues.has(optionValue)) return;
      if (isMaxSelectionsReached) return;

      onValueChange([...value, optionValue]);

      return;
    }

    onValueChange(value.filter(currentValue => currentValue !== optionValue));
  };
  const clearSelection = () => {
    onValueChange([]);
    setIsOpen(false);
    setSearchQuery('');
  };
  const clearFilterSelection = () => {
    onValueChange([]);
  };
  const removeValue = (optionValue: string) => {
    onValueChange(value.filter(currentValue => currentValue !== optionValue));
  };

  useEffect(() => {
    onSearchAPIRef.current = onSearchAPI;
  }, [onSearchAPI]);

  useEffect(() => {
    const search = onSearchAPIRef.current;

    if (!isOpen || !search) {
      return;
    }

    let isMounted = true;
    const timer = window.setTimeout(
      async () => {
        setIsSearching(true);

        try {
          const results = await search(searchQuery.trim());

          if (!isMounted) return;

          setApiOptions(results);
        } finally {
          if (isMounted) {
            setIsSearching(false);
          }
        }
      },
      searchQuery.trim() ? 300 : 0
    );

    return () => {
      isMounted = false;
      window.clearTimeout(timer);
    };
  }, [isOpen, searchQuery]);
  const triggerContent = (
    <>
      {icon}
      <span
        className={cn(
          'min-w-0 truncate',
          isChip && SELECT_CHIP_LABEL_CLASSNAME
        )}
      >
        {triggerLabel}
      </span>
      {showSelectedCountBadge && selectedOptions.length > 1 ? (
        <Badge className="h-5 min-w-5 rounded-full px-1.5 text-[11px] leading-none">
          +{selectedOptions.length - 1}
        </Badge>
      ) : null}
      {isChip ? (
        <span
          className="inline-flex size-4 shrink-0 items-center justify-center"
          aria-hidden
        >
          <ExpandArrowIcon
            className="size-[11px]"
            size={11}
            variant="filled"
          />
        </span>
      ) : (
        <ChevronDown className="size-4 shrink-0" aria-hidden />
      )}
    </>
  );

  return (
    <Popover
      modal={false}
      open={isOpen}
      onOpenChange={open => {
        if (open) {
          setOpenOptionOrder(mergedOptions.map(option => option.value));
        } else {
          setOpenOptionOrder([]);
        }

        setIsOpen(open);

        if (!open) setSearchQuery('');
      }}
    >
      {showSelectedAsTags ? (
        <PopoverTrigger asChild>
          <div
            role="combobox"
            aria-controls={listboxId}
            aria-expanded={isOpen}
            aria-haspopup="dialog"
            aria-label={`${placeholder}: ${selectedOptions.length} lựa chọn`}
            data-active={selectedOptions.length > 0}
            data-state={isOpen ? 'open' : 'closed'}
            data-disabled={disabled ? true : undefined}
            aria-disabled={disabled}
            tabIndex={disabled ? -1 : 0}
            className={cn(
              'border-alias-outline focus-visible:ring-ring flex min-h-9 w-full max-w-full min-w-0 cursor-pointer items-center justify-between gap-2 border bg-transparent px-3 py-3 text-sm font-normal shadow-none hover:bg-transparent focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:outline-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50',
              selectedOptions.length > 0
                ? 'text-on-surface'
                : cn('text-muted-foreground opacity-50', placeholderClassName),
              showSelectedAsTags ? 'rounded-2xl' : 'w-fit rounded-full px-2',
              className
            )}
          >
            {icon}
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
              {selectedOptions.length ? (
                selectedOptions.map(option => (
                  <span
                    key={option.value}
                    className={cn(
                      'inline-flex max-w-full items-center gap-1 font-medium',
                      tagVariant === 'outlined'
                        ? 'border-outline-variant bg-surface text-on-surface rounded-xl border px-2.5 py-1 text-sm'
                        : 'bg-surface-variant text-on-surface rounded-full px-2 py-1 text-xs'
                    )}
                  >
                    <span className="min-w-0 truncate font-normal">
                      {option.label}
                    </span>
                    <button
                      type="button"
                      aria-label={`Xóa ${option.label}`}
                      disabled={disabled}
                      className={cn(
                        '-mr-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full',
                        tagVariant === 'outlined'
                          ? 'text-on-surface-variant hover:bg-surface-variant hover:text-on-surface'
                          : 'text-on-surface-variant hover:bg-gray-10 hover:text-on-surface -mr-1'
                      )}
                      onClick={event => {
                        event.preventDefault();
                        event.stopPropagation();
                        removeValue(option.value);
                      }}
                    >
                      <X className="size-3.5" aria-hidden />
                    </button>
                  </span>
                ))
              ) : (
                <span className="min-w-0 truncate">{placeholder}</span>
              )}
            </div>
            <ChevronDown className="h-4 w-4 shrink-0 opacity-50" aria-hidden />
          </div>
        </PopoverTrigger>
      ) : canClear ? (
        <div
          data-active
          data-state={isOpen ? 'open' : 'closed'}
          className={cn(
            'border-alias-outline hover:border-primary bg-surface/95 text-gray-60 hover:text-primary data-[active=true]:border-primary data-[active=true]:text-primary data-[state=open]:border-primary data-[state=open]:text-primary inline-flex h-9 w-fit max-w-full min-w-0 items-center rounded-full border text-sm font-medium shadow-none',
            isChip && SELECT_CHIP_TRIGGER_CLASSNAME,
            className
          )}
        >
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              role="combobox"
              aria-expanded={isOpen}
              aria-haspopup="dialog"
              aria-label={`${placeholder}: ${selectedOptions.length} lựa chọn`}
              disabled={disabled}
              className={cn(
                'hover:bg-primary-variant hover:text-on-primary-variant h-full min-w-0 flex-1 justify-start gap-1 rounded-[inherit] border-0 bg-transparent px-3 py-2 text-sm font-medium text-inherit shadow-none',
                isChip &&
                'leading-xs gap-0.5 px-2 py-1.5 text-xs hover:bg-transparent hover:text-inherit has-[>svg]:px-2'
              )}
            >
              {triggerContent}
            </Button>
          </PopoverTrigger>

          <div className="border-outline-variant mr-1 flex h-6 shrink-0 items-center border-l pl-1">
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              disabled={disabled}
              aria-label={`Xóa tất cả lựa chọn của ${placeholder.toLocaleLowerCase('vi-VN')}`}
              onClick={clearSelection}
              className="text-gray-60 hover:bg-primary-variant hover:text-on-primary-variant size-7 rounded-xl"
            >
              <X className="size-4" aria-hidden />
            </Button>
          </div>
        </div>
      ) : (
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={isOpen}
            aria-haspopup="dialog"
            aria-label={`${placeholder}: ${selectedOptions.length} lựa chọn`}
            data-active={selectedOptions.length > 0}
            disabled={disabled}
            className={cn(
              'border-alias-outline hover:border-primary bg-surface/95 text-gray-60 hover:text-primary data-[active=true]:border-primary data-[active=true]:text-primary data-[state=open]:border-primary data-[state=open]:text-primary h-9 w-fit max-w-full min-w-0 justify-start gap-1 rounded-full border px-3 py-2 text-sm font-medium shadow-none hover:bg-transparent',
              isChip && SELECT_CHIP_TRIGGER_CLASSNAME,
              className
            )}
          >
            {triggerContent}
          </Button>
        </PopoverTrigger>
      )}

      <PopoverContent
        align="start"
        className={cn(
          'z-[100] flex max-h-[min(24rem,var(--radix-popover-content-available-height))] w-[var(--radix-popover-trigger-width)] flex-col overflow-hidden p-0',
          isFilterVariant &&
          'border-search-suggestions-outline bg-surface-pure shadow-search-suggestions max-h-[min(26.5rem,var(--radix-popover-content-available-height))] rounded-[18px]',
          contentClassName
        )}
        onWheel={event => event.stopPropagation()}
        onTouchMove={event => event.stopPropagation()}
      >
        {isSearch ? (
          <div
            className={cn(
              'flex items-center',
              isFilterVariant
                ? 'bg-gray-10 focus-within:border-base-ring m-2 min-h-10 shrink-0 gap-1.5 rounded-[14px] border border-transparent px-2 focus-within:[background:var(--filter-search-focus-background)]'
                : 'border-alias-outline border-b px-3'
            )}
          >
            <Search
              className={cn(
                'shrink-0',
                isFilterVariant
                  ? 'text-on-surface-variant size-5'
                  : 'mr-2 size-4 opacity-50'
              )}
              aria-hidden
            />
            <Input
              ref={searchInputRef}
              value={searchQuery}
              onChange={event => setSearchQuery(event.target.value)}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              className={cn(
                'placeholder:text-muted-foreground h-10 min-w-0 flex-1 rounded-none border-0 bg-transparent px-0 py-3 shadow-none focus-visible:ring-0',
                isFilterVariant
                  ? 'text-on-surface placeholder:text-on-surface-variant text-base leading-5 font-normal'
                  : 'text-sm'
              )}
            />
            {isFilterVariant && searchQuery ? (
              <button
                type="button"
                className="text-on-surface-variant hover:bg-gray-20 focus-visible:ring-primary inline-flex size-8 shrink-0 items-center justify-center rounded-full focus-visible:ring-2 focus-visible:outline-none"
                aria-label="Xóa nội dung tìm kiếm"
                onClick={() => {
                  setSearchQuery('');
                  searchInputRef.current?.focus();
                }}
              >
                <X className="size-5" aria-hidden />
              </button>
            ) : null}
          </div>
        ) : null}

        <div
          role="group"
          id={listboxId}
          aria-label={placeholder}
          className={cn(
            'min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain',
            isFilterVariant ? 'hide-scrollbar px-2 py-2' : 'space-y-1 p-1'
          )}
          onWheel={event => event.stopPropagation()}
          onTouchMove={event => event.stopPropagation()}
        >
          {isSearching && filteredOptions.length === 0 ? (
            <p
              className={cn(
                'flex items-center justify-center px-3 text-center font-normal',
                isFilterVariant
                  ? 'text-alias-content-disabled min-h-12 text-base leading-5'
                  : 'text-muted-foreground min-h-28 py-6 text-sm'
              )}
            >
              Đang tìm kiếm...
            </p>
          ) : filteredOptions.length === 0 ? (
            <p
              className={cn(
                'flex items-center justify-center px-3 text-center font-normal',
                isFilterVariant
                  ? 'text-alias-content-disabled min-h-12 text-base leading-5'
                  : 'text-muted-foreground min-h-28 py-6 text-sm'
              )}
            >
              {isFilterVariant && searchQuery.trim()
                ? `Không tìm thấy “${searchQuery.trim()}”.`
                : emptyText}
            </p>
          ) : (
            <>
              {isSearching ? (
                <p className="text-muted-foreground px-3 py-2 text-sm">
                  Đang tìm kiếm...
                </p>
              ) : null}
              {isFilterVariant && hasSelectedFilteredOptions ? (
                <div className="flex min-h-9 items-center justify-between px-4">
                  <span className="text-on-surface text-[13px] leading-[18px] font-normal">
                    Tiêu chí đang chọn
                  </span>
                  <button
                    type="button"
                    className="text-primary hover:bg-primary-variant focus-visible:ring-primary rounded-lg py-1 text-[13px] leading-[18px] font-medium focus-visible:ring-2 focus-visible:outline-none px-1"
                    onClick={clearFilterSelection}
                  >
                    Xoá tất cả
                  </button>
                </div>
              ) : null}
              {filteredOptions.map(option => {
                const optionId = `${inputId}-${encodeURIComponent(option.value)}`;
                const isChecked = selectedValues.has(option.value);
                const isOptionDisabled =
                  option.disabled || (isMaxSelectionsReached && !isChecked);

                return (
                  <Label
                    key={option.value}
                    htmlFor={optionId}
                    className={cn(
                      'cursor-pointer font-normal',
                      isFilterVariant
                        ? 'hover:bg-gray-10 flex min-h-11 items-center gap-2.5 rounded-xl px-4 py-2 text-base leading-5'
                        : 'hover:bg-gray-10 flex min-h-10 items-center gap-3 rounded-sm px-2 py-1.5 text-sm',
                      !isFilterVariant &&
                      isChecked &&
                      'bg-surface-variant text-on-surface',
                      isOptionDisabled &&
                      'text-content-disabled pointer-events-none cursor-not-allowed opacity-60'
                    )}
                  >
                    <Checkbox
                      id={optionId}
                      checked={isChecked}
                      disabled={isOptionDisabled}
                      onCheckedChange={checked =>
                        toggleValue(option.value, checked === true)
                      }
                      aria-label={option.label}
                      className={cn(
                        isFilterVariant && 'size-6 rounded-[8px] [&_svg]:size-3.5'
                      )}
                    />
                    <span className="min-w-0 flex-1 truncate">
                      {option.label}
                    </span>
                  </Label>
                );
              })}
            </>
          )}
        </div>

        {isMaxSelectionsReached && maxSelectionMessage ? (
          <div className="border-alias-outline bg-negative-variant border-t px-3 py-3">
            <div className="flex items-start gap-2">
              <TriangleAlert
                className="text-negative mt-0.5 size-4 shrink-0"
                aria-hidden="true"
              />
              <p className="text-on-negative-variant text-sm leading-5">
                {maxSelectionMessage}
              </p>
            </div>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
